import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '@heroui/react';
import cn from 'classnames';
import { useCheckResetTokenQuery, useSetPasswordMutation } from '@/features/Auth';
import { adminActions, resetApiCaches } from '@/entities/Admin';
import { useAppDispatch, useToast } from '@/shared/lib/hooks';
import { getPasswordRuleState, meetsPasswordRules } from '@/shared/lib/utils/passwordRules';
import { isPasswordResetTokenUsable } from '@/shared/lib/utils/passwordResetToken';
import { LocalStorageService } from '@/shared/services/localStorage.service';
import { Svg } from '@/shared/ui/Svg';
import { AppButton } from '@/shared/ui/AppButton';
import { Container } from '@/shared/ui/Container';
import { PageLoader } from '@/shared/ui/Loader';
import { routerPath } from '@/shared/config/router';
import LogoIcon from '@/shared/assets/icons/complex/logo/logo-black.complex.svg';
import SlashedEyeIcon from '@/shared/assets/icons/slashed-eye.svg';
import OpenedEyeIcon from '@/shared/assets/icons/opened-eye.svg';
import styles from './SetPasswordView.module.scss';

type LinkError = 'invalid' | 'blocked';

const linkErrorFromMessage = (message: string | undefined): LinkError | null => {
  if (message === 'Account is blocked. Please contact administrator.') return 'blocked';
  if (
    message === 'Invalid or expired token' ||
    message === 'Token has expired. Please request a new password reset link.'
  ) {
    return 'invalid';
  }
  return null;
};

export const SetPasswordView = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [setPassword, { isLoading }] = useSetPasswordMutation();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isNewPasswordVisible, setIsNewPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
  const [fieldError, setFieldError] = useState('');

  const token = searchParams.get('token');
  const isTokenWellFormed = isPasswordResetTokenUsable(token);
  const [linkError, setLinkError] = useState<LinkError | null>(
    isTokenWellFormed ? null : 'invalid',
  );
  const { data: tokenStatus, isLoading: isCheckingToken } = useCheckResetTokenQuery(token ?? '', {
    skip: !isTokenWellFormed,
  });

  const requirements = getPasswordRuleState(newPassword);
  const allRequirementsMet = meetsPasswordRules(newPassword);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword !== '';

  const handleSubmit = async () => {
    if (!token) {
      setLinkError('invalid');
      return;
    }
    if (!allRequirementsMet || !passwordsMatch) return;

    try {
      const result = await setPassword({ token, password: newPassword }).unwrap();
      const accessToken = result.data?.jwt;
      const user = result.data?.user;
      if (!accessToken || !user) {
        toast({
          title: 'Error',
          description: 'Failed to set password',
          color: 'danger',
        });
        return;
      }

      LocalStorageService.setAccessToken(accessToken);
      localStorage.setItem(
        'user',
        JSON.stringify({
          userId: user.id.toString(),
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
        }),
      );
      resetApiCaches();
      dispatch(adminActions.setProfile(null));
      dispatch(adminActions.setIsAuth(true));
      navigate(routerPath.main, { replace: true });
    } catch (error: any) {
      const message = error?.data?.message as string | undefined;
      const nextLinkError = linkErrorFromMessage(message);
      if (nextLinkError) {
        setLinkError(nextLinkError);
        return;
      }
      setFieldError(message || 'Failed to set password');
      toast({
        title: 'Error',
        description: message || 'Failed to set password',
        color: 'danger',
      });
    }
  };

  const checkedLinkError: LinkError | null =
    tokenStatus === 'blocked'
      ? 'blocked'
      : tokenStatus === 'invalid' || tokenStatus === 'expired'
        ? 'invalid'
        : null;
  const visibleLinkError = linkError ?? checkedLinkError;

  if (isCheckingToken) {
    return <PageLoader />;
  }

  if (visibleLinkError === 'blocked') {
    return (
      <div className={cn(styles.root)}>
        <Container className={styles.container}>
          <div className={styles.wrapper}>
            <Svg className={styles.logo} Icon={LogoIcon} />
            <h1 className={styles.title}>Account is blocked</h1>
            <p className={styles.message}>Please contact administrator.</p>
          </div>
        </Container>
      </div>
    );
  }

  if (visibleLinkError === 'invalid') {
    return (
      <div className={cn(styles.root)}>
        <Container className={styles.container}>
          <div className={styles.wrapper}>
            <Svg className={styles.logo} Icon={LogoIcon} />
            <h1 className={styles.title}>Link is invalid or expired</h1>
            <p className={styles.message}>
              This link cannot be used. Request a new one with Forgot password.
            </p>
            <AppButton
              className={styles.button}
              color="grey"
              onPress={() => navigate(routerPath.forgotPassword)}
            >
              Forgot password
            </AppButton>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className={cn(styles.root)}>
      <Container className={styles.container}>
        <div className={styles.wrapper}>
          <Svg className={styles.logo} Icon={LogoIcon} />
          <h1 className={styles.title}>Set Password</h1>
          <div className={styles.form}>
            <div className={styles.inputContainer}>
              <div className={styles.field}>
                <label className={styles.label}>Password</label>
                <Input
                  type={isNewPasswordVisible ? 'text' : 'password'}
                  placeholder="Create a password"
                  value={newPassword}
                  onValueChange={(value) => {
                    setNewPassword(value);
                    setFieldError('');
                  }}
                  variant="bordered"
                  radius="sm"
                  isInvalid={(newPassword !== '' && !allRequirementsMet) || !!fieldError}
                  classNames={{
                    inputWrapper: cn(
                      styles.inputWrapper,
                      (newPassword !== '' && !allRequirementsMet) || fieldError
                        ? 'border-red-500 focus-within:border-red-500'
                        : 'border-[#E5E0EB] hover:border-black-DEFAULT focus-within:border-black-DEFAULT',
                    ),
                  }}
                  endContent={
                    <button
                      type="button"
                      className={styles.eyeButton}
                      onClick={() => setIsNewPasswordVisible(!isNewPasswordVisible)}
                      aria-label="toggle password visibility"
                    >
                      <Svg
                        Icon={isNewPasswordVisible ? SlashedEyeIcon : OpenedEyeIcon}
                        width={20}
                        height={20}
                        className="text-grey-secondary"
                      />
                    </button>
                  }
                />
                {fieldError && <p className={styles.errorText}>{fieldError}</p>}
                {newPassword !== '' && !allRequirementsMet && (
                  <p className={styles.errorText}>
                    Please add all necessary characters to create safe password:
                  </p>
                )}
              </div>

              {newPassword !== '' && (
                <div className={styles.requirements}>
                  <ul className={styles.requirementsList}>
                    <li className={requirements.minLength ? styles.requirementMet : styles.requirement}>
                      Minimum characters 8
                    </li>
                    <li className={requirements.uppercase ? styles.requirementMet : styles.requirement}>
                      One uppercase character
                    </li>
                    <li className={requirements.lowercase ? styles.requirementMet : styles.requirement}>
                      One lowercase character
                    </li>
                    <li className={requirements.number ? styles.requirementMet : styles.requirement}>
                      One number
                    </li>
                  </ul>
                </div>
              )}

              <div className={styles.field}>
                <label className={styles.label}>Confirm Password</label>
                <Input
                  type={isConfirmPasswordVisible ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onValueChange={setConfirmPassword}
                  variant="bordered"
                  radius="sm"
                  isInvalid={confirmPassword !== '' && !passwordsMatch}
                  classNames={{
                    inputWrapper: cn(
                      styles.inputWrapper,
                      confirmPassword !== '' && !passwordsMatch
                        ? 'border-red-500 focus-within:border-red-500'
                        : 'border-[#E5E0EB] hover:border-black-DEFAULT focus-within:border-black-DEFAULT',
                    ),
                  }}
                  endContent={
                    <button
                      type="button"
                      className={styles.eyeButton}
                      onClick={() => setIsConfirmPasswordVisible(!isConfirmPasswordVisible)}
                      aria-label="toggle password visibility"
                    >
                      <Svg
                        Icon={isConfirmPasswordVisible ? SlashedEyeIcon : OpenedEyeIcon}
                        width={20}
                        height={20}
                        className="text-grey-secondary"
                      />
                    </button>
                  }
                />
                {confirmPassword !== '' && !passwordsMatch && (
                  <p className={styles.errorText}>Passwords must match</p>
                )}
              </div>
            </div>

            <AppButton
              className={styles.button}
              color="grey"
              onPress={handleSubmit}
              disabled={!allRequirementsMet || !passwordsMatch || isLoading}
            >
              {isLoading ? 'Loading...' : 'Save password'}
            </AppButton>
            <button type="button" className={styles.backLink} onClick={() => navigate(routerPath.login)}>
              Back to Sign in
            </button>
          </div>
        </div>
      </Container>
    </div>
  );
};
